import { CONFIG } from '../config.js';
import { normalizeStory } from './storyMapper.js';

/**
 * ─── DESIGN PATTERN: REPOSITORY ───────────────────────────────────────────
 *
 * Problema architetturale: le API di Hacker News richiedono due passaggi
 * (prima la lista degli ID, poi una chiamata per ogni dettaglio) e restituiscono
 * dati grezzi e incoerenti. Se questa logica finisse nei moduli di UI, ogni
 * componente dovrebbe conoscere endpoint, parallelizzazione e formato dei dati.
 *
 * Il Repository incapsula "da dove arrivano i dati e come sono fatti" ed espone
 * un'interfaccia di dominio (`getNewStoryIds`, `getStoriesByIds`). La UI non sa
 * nulla di HTTP, e i test possono sostituire il client con un doppio di test.
 *
 * Il client HTTP viene iniettato dal costruttore (Dependency Injection):
 * nessun `import axios` in questo file.
 */
export class HackerNewsRepository {
  #http;
  #cache = new Map();

  /**
   * @param {{ get: (path: string) => Promise<any> }} httpClient
   */
  constructor(httpClient) {
    if (!httpClient || typeof httpClient.get !== 'function') {
      throw new TypeError('HackerNewsRepository richiede un client HTTP con metodo get()');
    }
    this.#http = httpClient;
  }

  /**
   * Scarica l'elenco degli ID delle ultime news (~500 elementi).
   *
   * @returns {Promise<number[]>}
   */
  async getNewStoryIds() {
    const data = await this.#http.get(CONFIG.NEW_STORIES_PATH);
    if (!Array.isArray(data)) {
      throw new Error('Risposta inattesa da newstories: attesa una lista di ID');
    }
    return data.filter((id) => Number.isInteger(id));
  }

  /**
   * Scarica il dettaglio di una singola news (con cache in memoria).
   *
   * @param {number} id
   * @returns {Promise<object|null>} story normalizzata, `null` se non mostrabile
   */
  async getStoryById(id) {
    if (this.#cache.has(id)) return this.#cache.get(id);

    const raw = await this.#http.get(`/item/${id}.json`);
    const story = normalizeStory(raw);
    this.#cache.set(id, story);
    return story;
  }

  /**
   * Scarica in parallelo il dettaglio di più news.
   *
   * Usa `allSettled`: una singola chiamata fallita (o un item cancellato) non
   * deve impedire la visualizzazione delle altre nove news della pagina.
   *
   * @param {number[]} ids
   * @returns {Promise<{ stories: object[], failed: number }>}
   */
  async getStoriesByIds(ids = []) {
    if (!ids.length) return { stories: [], failed: 0 };

    const results = await Promise.allSettled(ids.map((id) => this.getStoryById(id)));

    const stories = [];
    let failed = 0;

    for (const result of results) {
      if (result.status === 'fulfilled') {
        if (result.value) stories.push(result.value);
      } else {
        failed += 1;
      }
    }

    return { stories, failed };
  }

  /** Svuota la cache: usata dal pulsante "Aggiorna". */
  clearCache() {
    this.#cache.clear();
  }
}
